/**
 * Storage API — Supabase Storage file upload and download operations.
 *
 * Handles uploading voice notes, attachments, and screenshots
 * to Supabase Storage buckets. Replaces the OneDrive storage layer.
 *
 * Buckets (created by the migration):
 *   - voice-notes
 *   - attachments
 *   - screenshots
 */

import supabase from './supabaseClient';

/**
 * Upload a file to a Supabase Storage bucket and return its public/signed URL.
 *
 * @param {string} bucket — bucket name
 * @param {string} path — file path within the bucket
 * @param {Blob|File} fileContent — the file data
 * @param {string} contentType — MIME type
 * @returns {Promise<string>} URL of the uploaded file
 */
async function uploadToStorage(bucket, path, fileContent, contentType) {
  const { error: uploadError } = await supabase.storage
    .from(bucket)
    .upload(path, fileContent, {
      contentType,
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`Storage upload error (${bucket}/${path}): ${uploadError.message}`);
  }

  // Generate a signed URL valid for 1 year (files are private)
  const { data: urlData, error: urlError } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 60 * 60 * 24 * 365);

  if (urlError) {
    throw new Error(`Failed to generate signed URL: ${urlError.message}`);
  }

  return urlData.signedUrl;
}

/**
 * Upload an attachment file.
 *
 * @param {string} fileId — the Inbox file ID (used as subfolder)
 * @param {File} file — the file to upload
 * @returns {Promise<string>} URL of the uploaded file
 */
export async function uploadAttachment(fileId, file) {
  const path = `${fileId}/${file.name}`;
  return uploadToStorage('attachments', path, file, file.type || 'application/octet-stream');
}

/**
 * Upload multiple attachments for a file.
 *
 * @param {string} fileId
 * @param {File[]} files
 * @returns {Promise<string[]>} array of uploaded file URLs
 */
export async function uploadAttachments(fileId, files) {
  const urls = [];
  for (const file of files) {
    const url = await uploadAttachment(fileId, file);
    urls.push(url);
  }
  return urls;
}

/**
 * Upload an atwork screenshot for a file.
 *
 * @param {string} fileId
 * @param {File} screenshot
 * @returns {Promise<string>} URL of the uploaded screenshot
 */
export async function uploadAtworkScreenshot(fileId, screenshot) {
  const ext = screenshot.name.split('.').pop() || 'png';
  const path = `${fileId}_atwork.${ext}`;
  return uploadToStorage('screenshots', path, screenshot, screenshot.type || 'image/png');
}

/**
 * Upload a voice note.
 *
 * @param {string} fileId
 * @param {Blob} audioBlob — the recorded audio blob
 * @returns {Promise<string>} URL of the uploaded voice note
 */
export async function uploadVoiceNote(fileId, audioBlob) {
  const path = `${fileId}.webm`;
  return uploadToStorage('voice-notes', path, audioBlob, 'audio/webm');
}

/**
 * Delete a voice note from storage.
 *
 * @param {string} fileId — the Inbox file ID
 */
export async function deleteVoiceNote(fileId) {
  const path = `${fileId}.webm`;
  const { error } = await supabase.storage.from('voice-notes').remove([path]);
  if (error) {
    console.warn(`Failed to delete voice note (${path}):`, error.message);
  }
}

/**
 * Delete all attachments for a file from storage.
 *
 * @param {string} fileId — the Inbox file ID (used as subfolder)
 */
export async function deleteAttachments(fileId) {
  const { data: files, error: listError } = await supabase.storage
    .from('attachments')
    .list(fileId);

  if (listError) {
    console.warn(`Failed to list attachments for ${fileId}:`, listError.message);
    return;
  }

  if (files && files.length > 0) {
    const paths = files.map((f) => `${fileId}/${f.name}`);
    const { error } = await supabase.storage.from('attachments').remove(paths);
    if (error) {
      console.warn(`Failed to delete attachments for ${fileId}:`, error.message);
    }
  }
}

/**
 * Delete the atwork screenshot for a file from storage.
 *
 * @param {string} fileId — the Inbox file ID
 */
export async function deleteScreenshot(fileId) {
  // The screenshot path uses a suffix — list files matching the fileId prefix
  const { data: files, error: listError } = await supabase.storage
    .from('screenshots')
    .list('', { search: `${fileId}_atwork` });

  if (listError) {
    console.warn(`Failed to list screenshots for ${fileId}:`, listError.message);
    return;
  }

  if (files && files.length > 0) {
    const paths = files.map((f) => f.name);
    const { error } = await supabase.storage.from('screenshots').remove(paths);
    if (error) {
      console.warn(`Failed to delete screenshots for ${fileId}:`, error.message);
    }
  }
}

/**
 * Delete all storage files associated with a file record.
 * Catches errors gracefully — not every file has voice notes, attachments, or screenshots.
 *
 * @param {string} fileId — the Inbox file ID
 */
export async function deleteAllFileStorage(fileId) {
  await Promise.allSettled([
    deleteVoiceNote(fileId),
    deleteAttachments(fileId),
    deleteScreenshot(fileId),
  ]);
}

export async function getFileDownloadUrl(bucket, path) {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, 60 * 60 * 24); // 24-hour signed URL

  if (error) {
    throw new Error(`Failed to get download URL: ${error.message}`);
  }

  return data.signedUrl;
}
